import { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'

import { lookupTeamTeeTimes } from '../lib/data.js'
import { watchQualifyingEvent, assignQualifyingEventTokens } from '../lib/qualifyingEvents.js'
import { lastNames } from '../lib/teamName.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { assignTeamTokens, teamPlayLink, relinkTeamTokens } from '../lib/teamTokens.js'
import TeamJoinCard from './TeamJoinCard.jsx'
const DEFAULT_HUB_COPIES = 8

export default function QRPanel({ tournamentId, flights, registrations = {}, tournamentInfo = {}, teeTimes = {}, photoAdmins = {} }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const [view, setView] = useState((flights && flights.length) ? 'tournament' : 'qualifying') // 'tournament' | 'qualifying'

  // Roster-driven per-team qualifying QR codes come from the linked qualifying
  // event (tournamentInfo.qualifyingEventId). Watch it, auto-mint a token for any
  // team lacking one (covers every team + any added later), and build the
  // per-team qualify + get-the-app link  /play?e=<eid>&tk=<token>.
  const qeid = tournamentInfo?.qualifyingEventId || null
  const [qEvent, setQEvent] = useState(null)
  useEffect(() => { if (!qeid) { setQEvent(null); return undefined } return watchQualifyingEvent(qeid, setQEvent) }, [qeid])
  const qeTeams = qEvent?.teams ? Object.entries(qEvent.teams).map(([id, t]) => ({ id, ...t })) : []
  const qeMissing = qeTeams.filter((t) => !t.accessToken).length
  const qeAutoRef = useRef(false)
  useEffect(() => {
    if (qeid && qeMissing > 0 && !qeAutoRef.current) {
      qeAutoRef.current = true
      Promise.resolve(assignQualifyingEventTokens(qeid, qEvent?.teams || {})).catch(() => {}).finally(() => { qeAutoRef.current = false })
    }
  }, [qeid, qeMissing]) // eslint-disable-line react-hooks/exhaustive-deps
  const teamQualifyLink = (tok) => `${origin}/play?e=${encodeURIComponent(qeid)}&tk=${encodeURIComponent(tok)}`
  const startType = tournamentInfo?.startType || 'shotgun'
  const cards = []
  let teamCount = 0

  for (const f of flights) {
    const teams = Object.entries(f.teams || {}).map(([id, v]) => ({ id, ...v }))
    teamCount += teams.length
    const seen = new Set()
    for (const t of teams) {
      if (seen.has(t.id)) continue
      const oppId = t.info?.opponentId
      if (oppId && f.teams?.[oppId] && oppId !== t.id) {
        seen.add(t.id)
        seen.add(oppId)
        cards.push({
          kind: 'pair',
          key: `pair-${f.id}-${t.id}`,
          flightId: f.id,
          flightName: f.info?.name,
          teamA: t,
          teamB: { id: oppId, ...f.teams[oppId] },
        })
      } else {
        seen.add(t.id)
        cards.push({
          kind: 'solo',
          key: `solo-${f.id}-${t.id}`,
          flightId: f.id,
          flightName: f.info?.name,
          team: t,
        })
      }
    }
  }

  // The Qualifying view now uses one shared QR for self-register, so we
  // no longer block the whole panel on teamCount — both view branches
  // render their own empty state below.

  const teamUrl = (flightId, teamId) =>
    `${origin}/score?t=${tournamentId}&f=${flightId}&team=${teamId}`
  // The APP-BIND deep link for a team — /play?t=&tk=<accessToken> — binds the
  // player to their team in the native app. Empty until the token is minted.
  const teamBindUrl = (team) =>
    team?.info?.accessToken ? teamPlayLink(origin, tournamentId, team.info.accessToken) : ''
  const qualifyingSingleUrl = `${origin}/qualify?t=${tournamentId}`
  // The Tournament Hub QR encodes /live — a stable short link that resolves the
  // current tournament and forwards to the hub, so a printed QR never goes stale.
  const liveUrl = `${origin}/live`
  const photoAdminEntries = Object.entries(photoAdmins || {})
  const hostLabel = (typeof window !== 'undefined' && window.location?.host) || ''
  const [hubCopies, setHubCopies] = useState(DEFAULT_HUB_COPIES)

  const pairCards = cards.filter((c) => c.kind === 'pair')
  const soloCards = cards.filter((c) => c.kind === 'solo')

  // TEST: per-team access QR codes. Flat list of every team (with its token).
  const teamRows = []
  for (const f of flights) {
    for (const [teamId, team] of Object.entries(f.teams || {})) {
      teamRows.push({ flightId: f.id, flightName: f.info?.name, teamId, team })
    }
  }
  const tokensAssigned = teamRows.filter((r) => r.team?.info?.accessToken).length
  const teamTokensMissing = teamRows.length - tokensAssigned
  // Auto-mint a per-team APP-BIND token for any team lacking one (covers every
  // team + any added later), so both QR codes always show on the scorecard sheet.
  const teamAutoRef = useRef(false)
  useEffect(() => {
    if (teamTokensMissing > 0 && !teamAutoRef.current) {
      teamAutoRef.current = true
      Promise.resolve(assignTeamTokens(tournamentId, flights)).catch(() => {}).finally(() => { teamAutoRef.current = false })
    }
  }, [teamTokensMissing, tournamentId]) // eslint-disable-line react-hooks/exhaustive-deps
  // Re-link-on-rebuild: on opening the sheet, re-point any token whose teamId has
  // churned (rebuild / re-import) to the current team by surname — so printed QRs
  // never dead-end. Idempotent; runs once per tournament view.
  useEffect(() => {
    relinkTeamTokens(tournamentId, flights).catch(() => {})
  }, [tournamentId]) // eslint-disable-line react-hooks/exhaustive-deps
  const [genMsg, setGenMsg] = useState('')
  const [genBusy, setGenBusy] = useState(false)
  // Web Backup: quickly find one team on demand (filter → the grid narrows; Print
  // then prints only what's shown, so you can print a single team's backup).
  const [backupFilter, setBackupFilter] = useState('')
  const backupRows = teamRows.filter(({ team, teamId }) => {
    const q = backupFilter.trim().toLowerCase()
    if (!q) return true
    return (team?.info?.name || teamId).toLowerCase().includes(q)
  })
  const generateTokens = async () => {
    setGenBusy(true); setGenMsg('')
    try {
      const created = await assignTeamTokens(tournamentId, flights)
      setGenMsg(created > 0 ? `Created ${created} new team code${created === 1 ? '' : 's'}.` : 'Every team already has a code.')
    } catch (e) {
      setGenMsg(e?.message || 'Could not generate codes.')
    }
    setGenBusy(false)
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center justify-between gap-4 no-print">
        <div>
          <h2 className="text-lg font-bold">
            {view === 'qualifying' ? 'Qualifying QR Codes' : 'Tournament Round QR Codes'}
          </h2>
          <p className="text-sm text-gray-500">
            {view === 'qualifying'
              ? 'One QR per team — links straight into the qualifying scorecard. Print these for pro-shop check-in, separate from the tournament round QRs.'
              : 'Each pairing prints as one card with both teams\' QR codes side-by-side. Cut along the dashed lines and hand each team their own.'}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            <button
              onClick={() => setView('tournament')}
              className={`px-3 py-1.5 font-bold ${view === 'tournament' ? 'bg-green-deep text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
              🏌️ Tournament Round
            </button>
            <button
              onClick={() => setView('qualifying')}
              className={`px-3 py-1.5 font-bold ${view === 'qualifying' ? 'bg-green-deep text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
              ⛳ Qualifying
            </button>
            {PBCC_PREMIUM ? (
              <button
                onClick={() => setView('team-identity')}
                className={`px-3 py-1.5 font-bold ${view === 'team-identity' ? 'bg-green-deep text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                🌐 Web Backup
              </button>
            ) : null}
          </div>
          <button className="btn-primary" onClick={() => window.print()}>🖨 Print all</button>
        </div>
      </div>

      {view === 'tournament' && teamCount === 0 && (
        <div className="card text-center text-gray-500 py-10">
          Add teams via the <b>Registration</b> tab to generate tournament-round QR codes.
        </div>
      )}

      {view === 'tournament' && teamCount > 0 && (
        <>
          {/* Tournament Hub QR — single QR for everyone, big & printable.
             Same multi-copy print system used by the Calcutta auction QR. */}
          <div className="card">
            <div className="flex flex-wrap justify-between items-start gap-3 no-print">
              <div>
                <h2 className="text-lg font-bold">🏌️ Tournament Hub QR</h2>
                <p className="text-sm text-gray-500 max-w-md mt-1">
                  One QR — share with everyone. Players, families, and friends scan this to follow the tournament live (leaderboard, tomorrow's tee times, Calcutta).
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 font-bold">
                  Copies:
                  <input
                    type="number"
                    min="1"
                    className="input w-16 text-center text-sm py-1"
                    value={hubCopies}
                    onChange={(e) => setHubCopies(Math.max(1, Number(e.target.value) || 1))}
                  />
                </label>
                <button className="btn-primary" onClick={() => window.print()}>🖨️ Print Room Copies</button>
              </div>
            </div>

            <div className="mt-3 bg-white border-4 border-dashed border-green-deep rounded-3xl p-5 text-center max-w-md mx-auto no-print">
              <div className="text-sm uppercase tracking-widest text-green-deep font-bold">🏌️ Tournament Hub</div>
              <div className="text-xl font-extrabold mt-1 text-green-deep">FOLLOW LIVE</div>
              <p className="text-sm text-gray-600 mt-1 leading-snug px-3">
                Leaderboard · Tee times · Calcutta — updated in real time.
              </p>
              <div className="my-4 flex justify-center">
                <QRCodeCanvas value={liveUrl} size={220} marginSize={3} fgColor="#000000" bgColor="#FFFFFF" />
              </div>
              <div className="text-sm text-gray-400 break-all font-mono">{liveUrl}</div>
            </div>

            <p className="mt-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-md mx-auto no-print">
              💡 <b>Post these at the bar, on tables, at the entrance</b> — anyone can scan to follow along all week.
            </p>

            {/* Print-only 2-column sheet of identical Hub QR copies. */}
            <div className="hidden print:grid grid-cols-2 gap-3 p-2">
              {Array.from({ length: Math.max(1, Number(hubCopies) || 1) }).map((_, i) => (
                <div
                  key={i}
                  className="border-4 border-dashed border-green-deep rounded-2xl p-3 text-center"
                  style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                  <div className="text-sm uppercase tracking-widest text-green-deep font-extrabold">🏌️ Tournament Hub</div>
                  <div className="text-base font-extrabold mt-0.5 text-green-deep leading-tight">Scan to follow live</div>
                  <div className="my-2 flex justify-center">
                    <QRCodeCanvas value={liveUrl} size={180} marginSize={3} fgColor="#000000" bgColor="#FFFFFF" />
                  </div>
                  <div className="text-sm text-gray-600 font-mono break-all">{hostLabel || liveUrl}</div>
                </div>
              ))}
            </div>
          </div>

          {soloCards.length > 0 ? (
            <div className="card no-print bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-900">
                <b>{soloCards.length} team{soloCards.length === 1 ? '' : 's'}</b> not yet paired.
                Use <b>⇄ Auto-pair</b> in <i>Flights & Teams</i> to assign opponents before printing.
              </p>
            </div>
          ) : null}

          {/* Per-Photo-Admin QRs (only when admins exist). Admin's photos
              skip the review queue and post straight to the wall. */}
          {photoAdminEntries.length > 0 ? (
            <div className="card no-print">
              <h3 className="text-sm uppercase tracking-widest text-gray-500 font-bold">📸 Photo Admin QRs</h3>
              <p className="text-sm text-gray-700 mt-1">
                Hand each photo admin their personal QR — their submissions skip the review queue and post directly to the wall.
              </p>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {photoAdminEntries.map(([key, a]) => {
                  const url = `${origin}/photos?t=${tournamentId}&role=admin&pin=${encodeURIComponent(a.pin || '')}`
                  return (
                    <div key={key} className="bg-white border-2 border-dashed border-amber-300 rounded-2xl p-4 text-center">
                      <div className="text-sm uppercase tracking-wider text-amber-700 font-bold">Photo Admin</div>
                      <div className="text-base font-extrabold mt-1">{a.name}</div>
                      <div className="text-sm text-gray-500 font-mono mt-0.5">PIN {a.pin}</div>
                      <div className="my-3 flex justify-center">
                        <QRCodeCanvas value={url} size={140} includeMargin />
                      </div>
                      <div className="text-sm text-gray-400 break-all">{url}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          <div className="card no-print">
            <h3 className="text-sm uppercase tracking-widest text-gray-500 font-bold">🏌️ Player Scorecards</h3>
            <p className="text-sm text-gray-700 mt-1">
              Hand each team their QR card. <b>Two codes per team:</b> <span style={{ color: '#166534', fontWeight: 700 }}>📱 Scan to use the app</span> (binds the player to their team) and <span style={{ color: '#374151', fontWeight: 700 }}>⛳ Scan to score / follow</span> (web scorecard, no app needed).
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {pairCards.map((c) => (
              <div
                key={c.key}
                className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-4">
                <div className="text-center mb-3">
                  <div className="text-sm uppercase tracking-wider text-green-deep font-bold">{c.flightName}</div>
                  <div className="text-sm text-gray-500 mt-0.5">Match Pairing</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <TeamQR
                    flightName={c.flightName}
                    team={c.teamA}
                    url={teamUrl(c.flightId, c.teamA.id)}
                    bindUrl={teamBindUrl(c.teamA)}
                    vsName={c.teamB.info?.name}
                    teeRows={lookupTeamTeeTimes(teeTimes, startType, c.flightId, c.teamA.id)}
                  />
                  <TeamQR
                    flightName={c.flightName}
                    team={c.teamB}
                    url={teamUrl(c.flightId, c.teamB.id)}
                    bindUrl={teamBindUrl(c.teamB)}
                    vsName={c.teamA.info?.name}
                    teeRows={lookupTeamTeeTimes(teeTimes, startType, c.flightId, c.teamB.id)}
                  />
                </div>
              </div>
            ))}
            {soloCards.map((c) => (
              <div
                key={c.key}
                className="bg-white border-2 border-dashed border-amber-300 rounded-2xl p-4 text-center">
                <div className="text-sm uppercase tracking-wider text-green-deep font-bold">{c.flightName}</div>
                <div className="text-lg font-extrabold mt-1">{lastNames(c.team.info?.name)}</div>
                <div className="text-sm text-amber-700 mt-0.5 font-bold">No opponent assigned</div>
                <div className="text-sm text-gray-500 mt-0.5">{(c.team.info?.players || []).join(' · ')}</div>
                <div className="flex justify-center gap-4 my-3 flex-wrap">
                  <QRBlock caption="📱 Scan to use the app" sub="Binds you to your team" value={teamBindUrl(c.team)} tone="green" />
                  <QRBlock caption="⛳ Scan to score / follow" sub="Web scorecard — no app needed" value={teamUrl(c.flightId, c.team.id)} tone="gray" />
                </div>
                <TeeTimeStrip rows={lookupTeamTeeTimes(teeTimes, startType, c.flightId, c.team.id)} />
              </div>
            ))}
          </div>
        </>
      )}

      {view === 'qualifying' && (
        <>
          {/* #1 — PUBLIC / SPECTATOR QR: one code for everyone → live web app. */}
          <div className="card no-print">
            <h3 className="text-sm uppercase tracking-widest text-gray-500 font-bold">📣 Public / Spectator QR</h3>
            <p className="text-sm text-gray-700 mt-1">
              One code for everyone — players, families, spectators. Opens the live web app:
              leaderboard, brackets, and Calcutta. Post it at the bar, on tables, and at the entrance.
            </p>
          </div>
          <div className="bg-white border-4 border-dashed border-green-deep rounded-3xl p-6 text-center max-w-md mx-auto" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div className="text-sm uppercase tracking-widest text-green-deep font-bold">📣 Follow Live</div>
            <div className="text-xl font-extrabold mt-1 text-green-deep">Leaderboard · Brackets · Calcutta</div>
            <div className="my-4 flex justify-center">
              <QRCodeCanvas value={liveUrl} size={220} marginSize={3} fgColor="#000000" bgColor="#FFFFFF" />
            </div>
            <div className="text-sm text-gray-400 break-all font-mono">{liveUrl}</div>
          </div>

          {/* #2 — PER-TEAM PLAYER QR: qualify + get-the-app, one per roster team. */}
          <div className="card no-print flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm uppercase tracking-widest text-gray-500 font-bold">🔑 Per-Team Player QR Codes</h3>
              <p className="text-sm text-gray-700 mt-1 max-w-xl">
                One code per team, auto-generated from the qualifying roster — any team added later
                gets its own automatically. Each opens that team to <b>start qualifying</b> and
                <b> get the app</b> (iPhone → App Store, Android → APK).
              </p>
              {qeid ? <p className="text-sm text-gray-500 mt-1">{qeTeams.length - qeMissing} of {qeTeams.length} teams have a code.</p> : null}
            </div>
            {qeid && qeTeams.length > 0 ? <button className="btn-primary" onClick={() => window.print()}>🖨 Print all</button> : null}
          </div>

          {!qeid ? (
            <div className="card text-center text-gray-500 py-8 no-print">
              No qualifying roster is linked to this tournament yet. Create or link a <b>Qualifying Event</b>
              and every team’s code appears here automatically.
            </div>
          ) : qeTeams.length === 0 ? (
            <div className="card text-center text-gray-500 py-8 no-print">
              The qualifying roster is empty — add teams to the qualifying event and their codes appear here.
            </div>
          ) : (
            <>
              <style>{`@media print { .team-join-card { page-break-after: always; break-after: page; } .team-join-card:last-child { page-break-after: auto; break-after: auto; } }`}</style>
              <div className="flex flex-col items-center gap-6">
                {qeTeams.map((t) => {
                  const label = [t.player1, t.player2].filter(Boolean).map((n) => String(n).trim().split(/\s+/).pop()).join(' / ') || t.teamName || t.id
                  const link = t.accessToken ? teamQualifyLink(t.accessToken) : ''
                  return link
                    ? <TeamJoinCard key={t.id} teamLabel={label} step1Url={link} step2Url={link} />
                    : <div key={t.id} className="text-sm text-amber-600 font-semibold no-print">Generating code for {(t.teamName || t.id)}…</div>
                })}
              </div>
            </>
          )}

          {/* Fallback: one shared self-register code for pro-shop check-in. */}
          <details className="card no-print">
            <summary className="text-sm font-bold text-gray-700 cursor-pointer">Or use one shared self-register code (pro-shop check-in)</summary>
            <div className="mt-3 flex flex-col items-center">
              <QRCodeCanvas value={qualifyingSingleUrl} size={180} includeMargin />
              <div className="text-sm text-gray-400 break-all font-mono mt-2">{qualifyingSingleUrl}</div>
            </div>
          </details>
        </>
      )}

      {PBCC_PREMIUM && view === 'team-identity' && (
        <>
          <div className="card no-print">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm uppercase tracking-widest text-gray-500 font-bold">🌐 Web Backup</h3>
                <p className="text-sm text-gray-700 mt-1">
                  On-demand backup for the rare player who <b>can’t install the app</b>. Each team’s web-app QR
                  opens the browser companion bound to that team. Have the player scan it <b>right off this screen</b>,
                  or print it. This is separate from the printed player cards.
                </p>
                <p className="text-sm text-gray-500 mt-1">{tokensAssigned} of {teamRows.length} teams have a code.</p>
              </div>
              <div className="flex items-center gap-2">
                <button className="btn-primary" onClick={generateTokens} disabled={genBusy}>
                  {genBusy ? 'Generating…' : 'Generate missing codes'}
                </button>
                {genMsg ? <span className="text-sm text-gray-600">{genMsg}</span> : null}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="text" value={backupFilter} onChange={(e) => setBackupFilter(e.target.value)}
                placeholder="Find a team…"
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56" />
              {backupFilter ? (
                <button className="text-sm text-gray-500 underline" onClick={() => setBackupFilter('')}>clear</button>
              ) : null}
              <button className="btn-primary" onClick={() => window.print()}>🖨 Print shown</button>
              <span className="text-sm text-gray-500">Filter to one team, then Print to print just that team’s backup.</span>
            </div>
          </div>

          {teamRows.length === 0 ? (
            <div className="card text-center text-gray-500 py-10">
              Add teams via the <b>Registration</b> tab to generate team codes.
            </div>
          ) : backupRows.length === 0 ? (
            <div className="card no-print text-center text-gray-500 py-8">No team matches “{backupFilter}”.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {backupRows.map(({ flightId, flightName, teamId, team }) => {
                const token = team?.info?.accessToken || ''
                const url = token ? teamPlayLink(origin, tournamentId, token) : ''
                return (
                  <div key={`${flightId}-${teamId}`} className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center">
                    <div className="inline-block text-sm uppercase tracking-widest font-extrabold text-white bg-green-deep rounded-full px-2 py-0.5">🌐 Web Backup</div>
                    <div className="text-sm uppercase tracking-wider text-green-deep font-bold mt-2">{flightName}</div>
                    <div className="text-base font-extrabold mt-0.5 leading-tight">{lastNames(team?.info?.name) || teamId}</div>
                    <div className="my-3 flex justify-center min-h-[160px] items-center">
                      {token
                        ? <QRCodeCanvas value={url} size={160} includeMargin />
                        : <span className="text-sm text-amber-600 font-semibold">No code yet — click “Generate”.</span>}
                    </div>
                    {token ? <div className="text-sm text-gray-400 break-all font-mono">{url}</div> : null}
                    {token ? <div className="text-sm text-gray-500 mt-1 no-print">Player scans to open the web app — no install.</div> : null}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function TeeTimeStrip({ rows }) {
  if (!rows || rows.length === 0) return null
  return (
    <div className="mt-2 text-sm font-bold text-gray-700 leading-tight space-y-0.5">
      {rows.map((r) => (
        <div key={r.day}>
          Day {r.day}: {r.startTime || '—'}
          {r.holeOrTime
            ? (r.isShotgun ? ` · Hole ${r.holeOrTime}` : ` · ${r.holeOrTime}`)
            : ''}
        </div>
      ))}
    </div>
  )
}

function TeamQR({ team, url, bindUrl, vsName, teeRows }) {
  return (
    <div className="text-center">
      <div className="text-base font-extrabold leading-tight">{lastNames(team.info?.name)}</div>
      <div className="text-sm text-gray-500 mt-0.5">{(team.info?.players || []).join(' · ')}</div>
      {vsName ? (
        <div className="text-sm text-gray-400 italic mt-0.5">vs {lastNames(vsName)}</div>
      ) : null}
      <div className="flex justify-center gap-4 mt-2 flex-wrap">
        <QRBlock caption="📱 Scan to use the app" sub="Binds you to your team" value={bindUrl} tone="green" />
        <QRBlock caption="⛳ Scan to score / follow" sub="Web scorecard — no app needed" value={url} tone="gray" />
      </div>
      <TeeTimeStrip rows={teeRows} />
    </div>
  )
}

// One labeled QR block — a captioned code. `tone` tints the caption so the
// APP-BIND code (green) and the SCORE code (gray) read apart at a glance.
function QRBlock({ caption, sub, value, tone = 'gray', size = 132 }) {
  const color = tone === 'green' ? '#166534' : '#374151'
  if (!value) {
    return (
      <div className="text-center" style={{ minWidth: size }}>
        <div className="text-sm font-bold" style={{ color }}>{caption}</div>
        <div className="my-2 flex justify-center items-center text-sm text-amber-600 font-semibold" style={{ height: size }}>
          Code generating…
        </div>
      </div>
    )
  }
  return (
    <div className="text-center" style={{ minWidth: size }}>
      <div className="text-sm font-bold" style={{ color }}>{caption}</div>
      <div className="text-xs text-gray-400">{sub}</div>
      <div className="my-1 flex justify-center">
        <QRCodeCanvas value={value} size={size} includeMargin />
      </div>
      <div className="text-xs text-gray-400 break-all" style={{ maxWidth: size + 30, margin: '0 auto' }}>{value}</div>
    </div>
  )
}
